import React from 'react';

/**
 * Action d'en-tête réduite à son icône. Le libellé n'apparaît qu'au survol
 * (ou au focus clavier) sous forme d'infobulle : il reste lisible pour qui
 * découvre la page, sans occuper la largeur d'un bouton texte.
 *
 * L'infobulle sort à gauche de l'icône parce que la barre de filtres suit
 * immédiatement l'en-tête et serait masquée par une infobulle placée dessous.
 */
function BoutonIcone({ libelle, onClick, disabled, primaire, children }) {
  const classes = ['devis-btn-icone'];
  if (primaire) classes.push('devis-btn-icone-primaire');

  return (
    <button
      type="button"
      className={classes.join(' ')}
      onClick={onClick}
      disabled={disabled}
      aria-label={libelle}
      data-libelle={libelle}
    >
      {children}
    </button>
  );
}

/** Enveloppe : paramétrage de l'email envoyé depuis la page. */
export function IconeEnveloppe() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2.5" y="5" width="19" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}

/** Flèche descendant dans un bac : import de données extérieures. */
export function IconeImport() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3v12" />
      <path d="M7.5 10.5L12 15l4.5-4.5" />
      <path d="M4 17v2.5h16V17" />
    </svg>
  );
}

/** Page de texte : création d'un élément depuis un texte collé, relu avant d'être posé. */
export function IconeDepuisTexte() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 3h8l5 5v13H6z" />
      <path d="M14 3v5h5" />
      <path d="M9 12h7M9 15.5h7M9 19h4" />
    </svg>
  );
}

/** Plus : création d'un élément. */
export function IconePlus() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** Crayon : modifier l'élément d'une ligne. */
export function IconeCrayon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 20h4l10.5-10.5a2.1 2.1 0 0 0-3-3L5 17v3z" />
      <path d="M13.5 6.5l3 3" />
    </svg>
  );
}

/** Corbeille : retirer un élément (une ligne de devis, par exemple). */
export function IconeCorbeille() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 7h16" />
      <path d="M9.5 7V4.5h5V7" />
      <path d="M6.5 7l1 13h9l1-13" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

/** Immeuble : la fiche entreprise du client. */
export function IconeBatiment() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="3.5" width="12" height="17" rx="1.5" />
      <path d="M16 9.5h3.5a1 1 0 011 1v10H16" />
      <path d="M7.5 7.5h2M10.5 7.5h2M7.5 11h2M10.5 11h2M7.5 14.5h2M10.5 14.5h2" />
      <path d="M9 20.5v-3h2v3" />
    </svg>
  );
}

/** Groupe : la composition du CSE. */
export function IconeGroupe() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 19.5c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M16 14.2c2.8 0 5 1.9 5 4.8" />
    </svg>
  );
}

/** Calendrier : période, lieu et format de la formation. */
export function IconeCalendrier() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3.5" y="5" width="17" height="15.5" rx="2" />
      <path d="M3.5 10h17M8 3v4M16 3v4" />
      <path d="M8 14h3M13 14h3M8 17.5h3" />
    </svg>
  );
}

/** Lignes de texte : les textes libres du devis. */
export function IconeTexte() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M4 6h16M4 10.5h16M4 15h10M4 19.5h7" />
    </svg>
  );
}

/** Liste cochée : les lignes chiffrées du devis. */
export function IconeListe() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 6.5h11M9 12h11M9 17.5h11" />
      <path d="M3.5 6.5l1.2 1.2 2.3-2.4M3.5 12l1.2 1.2 2.3-2.4M3.5 17.5l1.2 1.2 2.3-2.4" />
    </svg>
  );
}

/** Coche : enregistrer, valider. */
export function IconeCoche() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
  );
}

export default BoutonIcone;
